import React, { useState, useCallback } from 'react';
import Lottie from 'lottie-react';
import zonePowerOnAnim from '../../animations/zonePowerOn.json';
import { motion, AnimatePresence } from 'framer-motion';
import SubstationIsometric from '../substation/SubstationIsometric';

// ─── Zone definitions ─────────────────────────────────────────────────────────
const ZONES = [
  { id: 'safety_monitoring',  label: 'Safety Monitoring'  },
  { id: 'power_distribution', label: 'Power Distribution' },
  { id: 'comms_tower',        label: 'Comms Tower'        },
  { id: 'data_logging',       label: 'Data Logging'       },
];

const ZONE_STATES = ['offline', 'selected', 'online'];
const SLOT_COUNT  = 4;

// ─── Help Modal ───────────────────────────────────────────────────────────────

function HelpModal({ onClose }) {
  return (
    <AnimatePresence>
      <motion.div
        key="help-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm pt-16"
        onClick={onClose}
      >
        <motion.div
          key="help-panel"
          initial={{ opacity: 0, scale: 0.95, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 8 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md mx-4 bg-gray-950 border border-cyan-700/50 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden flex flex-col max-h-[90vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-cyan-900/50 bg-cyan-950/60">
            <div className="flex items-center gap-2.5">
              <span className="text-cyan-300 font-bold text-base">?</span>
              <span className="text-sm font-semibold text-cyan-200 tracking-wide">How to Play — Substation Triage</span>
            </div>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-300 text-lg leading-none transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4 flex flex-col gap-4 text-sm text-slate-300 leading-relaxed overflow-y-auto">
            <p>
              A power fault has hit the substation. Four zones are offline — you must decide the order in which they get restored. Choose wisely: wrong sequencing can cascade into wider grid failure.
            </p>

            <ol className="flex flex-col gap-3 list-none">
              {[
                ['Assess each zone', 'Scan the isometric view. Each zone shows an amber indicator dome when offline. The zone label appears in the top-left corner of its tile.'],
                ['Click to queue', 'Click a zone to add it to the restoration queue. The first click marks it selected (blue), a second click marks it ready (green). Its queue position appears on the tile.'],
                ['Set the priority order', 'The order you click determines the restoration sequence shown in the Priority Queue above. Click a filled queue slot or the zone itself to remove it and re-sequence.'],
                ['Confirm the sequence', 'Once all four zones are queued, click "Confirm Restoration Sequence" to lock in your order and proceed.'],
              ].map(([title, desc], i) => (
                <li key={i} className="flex gap-3">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-900/80 border border-cyan-600/60 text-cyan-300 text-[11px] font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <div>
                    <div className="font-semibold text-slate-100 mb-0.5">{title}</div>
                    <div className="text-slate-400 text-xs">{desc}</div>
                  </div>
                </li>
              ))}
            </ol>

            <div className="px-3 py-2.5 rounded-lg border border-amber-800/40 bg-amber-950/30 text-amber-300/90 text-xs">
              <span className="font-semibold">Tip:</span> Safety-critical systems (like Safety Monitoring) should typically be restored before communications or data systems to limit cascading risk.
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * SubstationTriage
 *
 * Props:
 *   onComplete()                   — called when all zones are queued
 *   onZoneChange(zoneId, newState) — optional, fired on each state change
 *   isReplay                       — true if this step was already scored; enables skip
 */
function SubstationTriage({ onComplete, onZoneChange, isReplay }) {
  const initialZoneState = Object.fromEntries(ZONES.map(z => [z.id, 'offline']));
  const [zoneStates, setZoneStates] = useState(initialZoneState);
  const [queue, setQueue]           = useState(Array(SLOT_COUNT).fill(null));
  const [showHelp, setShowHelp]     = useState(false);
  const [showRestorationAnim, setShowRestorationAnim] = useState(false);

  const handleConfirm = useCallback(() => {
    ZONES.forEach(z => {
      if (onZoneChange) onZoneChange(z.id, 'online');
    });
    setZoneStates(Object.fromEntries(ZONES.map(z => [z.id, 'online'])));
    setShowRestorationAnim(true);
  }, [onZoneChange]);

  const allQueued = queue.filter(Boolean).length === ZONES.length;

  // 1-based queue position for each zone (null if not queued)
  const queueOrder = Object.fromEntries(
    ZONES.map(z => [z.id, queue.includes(z.id) ? queue.indexOf(z.id) + 1 : null])
  );

  // Click a zone: if already queued → dequeue; otherwise → cycle & enqueue.
  const cycleZone = useCallback((zoneId) => {
    const isQueued = queue.some(s => s === zoneId);

    if (isQueued) {
      setQueue(prev => {
        const filtered = prev.filter(s => s !== zoneId);
        return [...filtered, ...Array(SLOT_COUNT - filtered.length).fill(null)];
      });
      setZoneStates(prev => {
        if (onZoneChange) onZoneChange(zoneId, 'offline');
        return { ...prev, [zoneId]: 'offline' };
      });
    } else {
      setZoneStates(prev => {
        const cur  = prev[zoneId];
        const next = ZONE_STATES[(ZONE_STATES.indexOf(cur) + 1) % ZONE_STATES.length];
        // 'selected' is an intermediate visual state — don't degrade the global grid node
        if (onZoneChange && next !== 'selected') onZoneChange(zoneId, next);
        return { ...prev, [zoneId]: next };
      });
      setQueue(prev => {
        const nextEmpty = prev.findIndex(s => s === null);
        if (nextEmpty === -1) return prev;
        const updated = [...prev];
        updated[nextEmpty] = zoneId;
        return updated;
      });
    }
  }, [queue, onZoneChange]);

  const removeFromQueue = useCallback((slotIdx) => {
    const zoneId = queue[slotIdx];
    if (!zoneId) return;
    setQueue(prev => {
      const filtered = prev.filter(s => s !== zoneId);
      return [...filtered, ...Array(SLOT_COUNT - filtered.length).fill(null)];
    });
    setZoneStates(prev => {
      if (onZoneChange) onZoneChange(zoneId, 'offline');
      return { ...prev, [zoneId]: 'offline' };
    });
  }, [queue, onZoneChange]);

  return (
    <div style={{ background: '#0f172a', minHeight: '100vh', padding: '24px', fontFamily: 'system-ui, sans-serif' }}>
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

      {/* ── Title + Help button ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h2 style={{ color: '#e2e8f0', fontSize: 20, fontWeight: 700, letterSpacing: '0.05em' }}>
          SUBSTATION TRIAGE — ISOMETRIC VIEW
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isReplay && (
            <button
              onClick={onComplete}
              title="Skip (already completed)"
              className="border border-slate-600 bg-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-400 text-[11px] font-semibold px-3 py-1 rounded transition-colors"
            >
              Skip
            </button>
          )}
          <button
            onClick={() => setShowHelp(true)}
            title="How to play"
            className="w-6 h-6 rounded-full border border-cyan-700/60 bg-cyan-950/60 text-cyan-400 hover:text-cyan-200 hover:border-cyan-500 text-[12px] font-bold flex items-center justify-center transition-colors"
          >
            ?
          </button>
        </div>
      </div>

      {/* ── Two-column body ── */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* LEFT — isometric zone grid */}
        <div style={{ flex: '1 1 0', minWidth: 0, background: '#0d1b2a', borderRadius: 8, padding: 8 }}>
          <SubstationIsometric
            zoneStates={zoneStates}
            onZoneClick={cycleZone}
            queueOrder={queueOrder}
          />
        </div>

        {/* RIGHT — queue panel */}
        <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>

          <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', margin: 0, textTransform: 'uppercase' }}>
            Restoration Priority Queue
          </h3>

          {/* Confirm CTA — above queue slots once all zones are queued */}
          {allQueued && !showRestorationAnim && (
            <>
              <style>{`
                @keyframes cgPulseGlow {
                  0%   { box-shadow: 0 0 4px 1px #22d3ee22; }
                  50%  { box-shadow: 0 0 10px 3px #22d3ee44; }
                  100% { box-shadow: 0 0 4px 1px #22d3ee22; }
                }
              `}</style>
              <button
                onClick={handleConfirm}
                style={{
                  background: 'linear-gradient(135deg, #0e7490 0%, #0891b2 60%, #06b6d4 100%)',
                  border: '2px solid #22d3ee',
                  borderRadius: 8,
                  color: '#ffffff',
                  fontSize: 13,
                  fontWeight: 800,
                  padding: '10px 0',
                  cursor: 'pointer',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  animation: 'cgPulseGlow 1.8s ease-in-out infinite',
                  width: '100%',
                  transition: 'transform 0.18s ease, filter 0.18s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.filter = 'brightness(1.18)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)';    e.currentTarget.style.filter = 'brightness(1)'; }}
              >
                ✓ Confirm Restoration Sequence
              </button>
            </>
          )}

          {/* Restoration animation */}
          {showRestorationAnim && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Lottie
                animationData={zonePowerOnAnim}
                loop={false}
                autoplay
                onComplete={onComplete}
                style={{ width: 48, height: 48 }}
              />
              <span style={{ color: '#22d3ee', fontSize: 12, fontWeight: 600 }}>
                Restoring power…
              </span>
            </div>
          )}

          {/* Queue slots — vertical stack */}
          {queue.map((zoneId, idx) => {
            const zone = ZONES.find(z => z.id === zoneId);
            return (
              <div
                key={idx}
                onClick={() => zoneId !== null && removeFromQueue(idx)}
                style={{
                  width: '100%',
                  height: 52,
                  border: `2px solid ${zoneId !== null ? '#60a5fa' : '#334155'}`,
                  borderRadius: 6,
                  background: zoneId !== null ? '#1e3a5f' : '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '0 12px',
                  cursor: zoneId !== null ? 'pointer' : 'default',
                  transition: 'background 0.2s, border-color 0.2s',
                  boxSizing: 'border-box',
                }}
                role={zoneId !== null ? 'button' : undefined}
                aria-label={
                  zoneId !== null
                    ? `Remove ${zone?.label} from queue slot ${idx + 1}`
                    : `Empty queue slot ${idx + 1}`
                }
              >
                <span style={{ fontSize: 10, fontWeight: 700, color: '#475569', letterSpacing: '0.05em', flexShrink: 0 }}>
                  #{idx + 1}
                </span>
                <span style={{ fontSize: 11, fontWeight: zoneId !== null ? 600 : 400, color: zoneId !== null ? '#bfdbfe' : '#475569' }}>
                  {zone ? zone.label : '— empty —'}
                </span>
              </div>
            );
          })}

          {/* Dequeue hint */}
          {queue.some(Boolean) && !allQueued && (
            <p style={{ color: '#64748b', fontSize: 10, margin: 0 }}>
              Click a filled slot or the zone to dequeue it.
            </p>
          )}

        </div>
      </div>
    </div>
  );
}

export default SubstationTriage;
